// External Imports
import { useContext, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import { MdAdd } from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import { UtilityContext } from "../../context/util-context";
import { useLocation } from "react-router-dom";
import Radio from "../UI/Radio";
import { isoDateToString } from "../../util/format-date";
import PaginationMenu from "../UI/PaginationMenu";
import SearchBar from "../UI/SearchBar";
import DateFilter from "../UI/DateFilter";
import FilterMenu from "../UI/FilterMenu";
import ListLabels from "../UI/ListLabels";
import ExportCsvButton from "../UI/ExportCsvButton";

// Internal Imports

/** ========================================================================
 * * Claims Component
 * This component renders a list of all claims.
 * ========================================================================== */

export default function Claims() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    status: "All",
    query: "",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState(paramFilters);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      // Send Request
      const data = await requestAdminAPI("search", {
        table: "claim",
        limit,
        filters: filter,
      });

      // Replace URL
      let url = "/claims?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setClaims(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Status",
      options: ["All", "Active", "Denied", "Completed"],
      selected: filter.status,
    },
  ];

  // Calculate Total and Average
  let paid = 0;
  let approved = 0;
  claims.forEach((claim) => {
    paid += Number(claim.amountPaid);
    approved += Number(claim.amountApproved);
  });

  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <Heading
        title="Claims"
        text="Denied claims are red, completed claims are green and active are gray. Go to a booking 
        information page and click the add claim button or click the '+' icon on the top right 
        to add a new claim."
      >
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <ExportCsvButton array={claims} filename="claims" />
      </Heading>
      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />
      {loading ? (
        <LoadingCard columns={5} max={4} />
      ) : claims.length > 0 ? (
        <div className="list">
          <div className="labels lg:grid-cols-8">
            <ListLabels
              labels={[
                { classes: "w-24", name: "Image" },
                { name: "Date Created" },
                { classes: "lg:col-span-2", name: "Vehicle" },
                "Driver",
                { classes: "lg:col-span-2", name: "Location" },
                "Incident Date",
              ]}
              selected="Date Created"
              setSelected={() => {}}
            />
          </div>
          {claims.map((claim, idx) => (
            <ClaimCard key={idx} claim={claim} navigate={navigate} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        count={claims.length}
        limit={limit}
        setLimit={setLimit}
        loading={loading}
      >
        <p className="text-stone-400">
          Total Paid:{" "}
          <span className="font-semibold text-stone-200">
            ${paid.toFixed(2)}
          </span>
        </p>
        <p className="text-stone-400">
          Total Approved:{" "}
          <span className="font-semibold text-stone-200">
            ${approved.toFixed(2)}
          </span>
        </p>
      </PaginationMenu>
    </div>
  );
}

const ClaimCard = ({ claim, navigate }) => {
  return (
    <div
      onClick={(e) => navigate(`/claims/info?id=${claim.id}`, e)}
      className={`${
        claim?.status === "denied"
          ? "border-red-500"
          : claim.status === "completed"
          ? "border-green-500"
          : ""
      } grid lg:grid-cols-8 pill`}
    >
      <img src={claim.vehicle.cornerImage} className="object-contain w-24" />
      <p className="text-stone-400">{isoDateToString(claim.dateCreated)}</p>
      <p className="font-semibold lg:col-span-2">
        {claim.vehicle.year} {claim.vehicle.make.name} {claim.vehicle.model}{" "}
        {claim.vehicle.vin.substring(13, 17)}
      </p>
      <p className="">{claim.driverName}</p>
      <p className="lg:col-span-2 text-stone-400">{claim.location}</p>
      <p>{isoDateToString(claim.incidentDate)}</p>
    </div>
  );
};
